import moment from 'moment';
import { pickAmazonPrice } from './amazon-price';
import { calcFbaProfit } from './fba-profit';

/**
 * 商品共有（せどりすと）の仕入れ実績 1 件。
 * ヤマダ・エディオンの在庫一覧に「実績 誰が いつ 仕入値 → 出品予定（利益）」として出す（2026-10-14 ユーザー指定）。
 * 在庫一覧のどの商品にも付かないものは、画面の下に在庫一覧と同じ形（在庫状況は無し）で出す（2026-10-14 ユーザー指定）
 */
export type MemberPurchase = {
  id: number;
  userName: string;
  /** 仕入日（M/D） */
  buyingDate: string;
  buyingPrice: number;
  sellingPrice: number;
  /** 出品予定価格で売った場合の利益額。商品共有の画面と同じ式（出品予定 − 仕入値 − 手数料。無い手数料は 0） */
  profitPrice: number;
  asin: string;
  jan: string;
  productName: string;
  /** ここから下は、画面の下の一覧を在庫一覧と同じ形で出すための項目 */
  imageUrl: string;
  /** 在庫一覧と同じ決め方の Amazon 価格（Amazon と Keepa の新しい方。無い時は -1） */
  amazonPrice: number;
  amazonPriceSource: 'current' | 'last' | 'none' | 'stale';
  /** amazonPriceSource が last の時だけ: その価格があった日（M/D） */
  lastKnownPriceAt?: string;
  boughtPastMonth?: number | null;
  /** Amazon 価格で FBA で売った場合の利益額・利益率（在庫一覧と同じ式。仕入値はこの実績の仕入値）。出せない時は null */
  fbaProfitPrice: number | null;
  fbaProfitRate: number | null;
};

/** 在庫一覧に付ける実績は直近この日数 */
export const MEMBER_PURCHASE_DAYS = 30;
/** 1 商品に出す実績の上限 */
export const MEMBER_PURCHASES_PER_PRODUCT = 3;

type LastKnownPrice = { price: number; at: string };

/** 商品共有 API（userSedoristProducts）の 1 行のうち、使う項目 */
export type SedoristRow = {
  id: number;
  user: { name: string };
  amazonProduct: {
    asin: string;
    imageUrl?: string | null;
    salesCommission?: number | null;
    categoryFee?: number | null;
    fbaShippingFee?: number | null;
    latestPrice?: number | null;
    lastKnownPrice?: LastKnownPrice | null;
    keepaProduct?: {
      boughtPastMonth?: number | null;
      latestPrice?: number | null;
      lastKnownPrice?: LastKnownPrice | null;
      keepaUpdatedAt?: string | null;
    } | null;
  };
  product?: { jan?: string | null } | null;
  productName: string;
  buyingPrice: number;
  sellingPrice: number;
  buyingDate: string;
  buyingStore?: { name: string } | null;
};

/** 商品共有の行から、仕入店舗が storeName のものだけを実績にする。仕入日の新しい順 */
export function toMemberPurchases(rows: readonly SedoristRow[], storeName: string): MemberPurchase[] {
  return rows
    .filter((r) => r.buyingStore?.name === storeName)
    .map((r) => {
      const ap = r.amazonProduct;
      const fees = (ap.salesCommission ?? 0) + (ap.categoryFee ?? 0) + (ap.fbaShippingFee ?? 0);
      const at = moment(new Date(r.buyingDate));
      // 画面の下の一覧用: 在庫一覧と同じ決め方の Amazon 価格と、この実績の仕入値で FBA で売った場合の利益
      const amazon = pickAmazonPrice(ap);
      const fba = calcFbaProfit({
        amazonPrice: amazon.price,
        cost: r.buyingPrice,
        salesCommission: ap.salesCommission,
        categoryFee: ap.categoryFee,
        fbaShippingFee: ap.fbaShippingFee,
      });
      return {
        purchase: {
          id: r.id,
          userName: r.user?.name ?? '',
          buyingDate: at.isValid() ? at.format('M/D') : '',
          buyingPrice: r.buyingPrice,
          sellingPrice: r.sellingPrice,
          profitPrice: r.sellingPrice - r.buyingPrice - fees,
          asin: ap.asin,
          jan: r.product?.jan ?? '',
          productName: r.productName,
          imageUrl: ap.imageUrl ?? '',
          amazonPrice: amazon.price,
          amazonPriceSource: amazon.source,
          lastKnownPriceAt: amazon.at,
          boughtPastMonth: ap.keepaProduct?.boughtPastMonth,
          fbaProfitPrice: fba.profitPrice,
          fbaProfitRate: fba.profitRate,
        },
        sortKey: at.isValid() ? at.valueOf() : 0,
      };
    })
    .sort((a, b) => b.sortKey - a.sortKey)
    .map((x) => x.purchase);
}

/**
 * 在庫一覧の各商品に実績を付ける（JAN が同じもの。無ければ ASIN が同じもの）。1 商品あたり MEMBER_PURCHASES_PER_PRODUCT 件まで。
 * 戻り値は、どの商品にも付かなかった実績（改廃リストに無い商品。画面の下の一覧に出す）
 */
export function attachMemberPurchases<T extends { jan: string; asin: string; memberPurchases?: MemberPurchase[] }>(
  products: T[],
  purchases: MemberPurchase[],
): MemberPurchase[] {
  const byJan = new Map<string, MemberPurchase[]>();
  const byAsin = new Map<string, MemberPurchase[]>();
  for (const m of purchases) {
    if (m.jan) byJan.set(m.jan, [...(byJan.get(m.jan) ?? []), m]);
    if (m.asin) byAsin.set(m.asin, [...(byAsin.get(m.asin) ?? []), m]);
  }
  const used = new Set<number>();
  for (const p of products) {
    const list = (p.jan && byJan.get(p.jan)) || (p.asin && byAsin.get(p.asin)) || [];
    p.memberPurchases = list.slice(0, MEMBER_PURCHASES_PER_PRODUCT);
    for (const m of list) used.add(m.id);
  }
  return purchases.filter((m) => !used.has(m.id));
}
